/* eslint-disable max-len */
import React, { useRef, useEffect, useState } from 'react'

interface IPRToothIconProps {
  id: number
  active: boolean
  width?: number | string
  height?: number | string
}

const getIPRColor = (
  active: boolean,
): {
  fill: string
  stroke: string
  backgroundColor: string
} => ({
  fill: active ? '#F59E0B' : 'none',
  stroke: active ? 'none' : '#CDCDCD',
  backgroundColor: active ? '#FEF3C7' : '#FFFFFF',
})

export const ToothGradientDefsTopToBottom = (): JSX.Element => (
  <defs>
    <filter id="toothShadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow
        dx="0"
        dy="1"
        stdDeviation="1"
        floodColor="#999"
        floodOpacity="0.3"
      />
    </filter>
    <linearGradient id="toothGradientTopToBottom" x1="0" y1="1" x2="0" y2="0">
      <stop offset="0%" stopColor="#FFFFFF" />
      <stop offset="40%" stopColor="#FFFFFF" />
      <stop offset="41%" stopColor="#C3AC71" />
      <stop offset="100%" stopColor="#C3AC71" />
    </linearGradient>
  </defs>
)

export const ToothGradientDefsBottomToTop = (): JSX.Element => (
  <defs>
    <filter id="toothShadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow
        dx="0"
        dy="1"
        stdDeviation="1"
        floodColor="#999"
        floodOpacity="0.3"
      />
    </filter>
    <linearGradient id="toothGradientBottomToTop" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#FFFFFF" />
      <stop offset="40%" stopColor="#FFFFFF" />
      <stop offset="41%" stopColor="#C3AC71" />
      <stop offset="100%" stopColor="#C3AC71" />
    </linearGradient>
  </defs>
)

const realisticStroke = {
  stroke: '#8A7D6B',
  strokeOpacity: 0.4,
  strokeWidth: 0.5,
}

const GradientDefs = ({ id }: { id: number }): JSX.Element =>
  id <= 16 ? <ToothGradientDefsTopToBottom /> : <ToothGradientDefsBottomToTop />

const toothIcons: { [key: number]: JSX.Element } = {
  1: (
    <>
      <path
        d="M10.3567 24.493C12.259 23.4207 12.6826 22.1464 12.6717 20.8359C12.6717 19.7181 12.3218 18.9838 12.1469 18.7564C11.1498 17.3787 10.7912 15.6263 10.7365 14.9223C10.3211 12.8104 9.43766 8.2485 9.22774 6.89683C9.01783 5.54517 8.33122 3.14943 8.01416 2.12052C7.85016 1.66563 7.39753 0.755859 6.89897 0.755859C6.54513 0.755859 6.35761 1.08321 6.27075 1.3894C6.20766 1.61176 6.22033 1.84422 6.23777 2.07469C6.43361 4.66262 6.58958 9.8536 6.21018 9.8536C5.19347 9.8536 4.931 3.4202 4.50461 2.44544C4.25784 1.88131 3.91609 1.65727 3.6383 1.58879C3.4556 1.54375 3.27443 1.62682 3.21019 1.80368C2.92705 2.58324 2.73351 4.97981 2.73351 4.97981C2.33999 7.35172 2.09514 13.5577 2.17584 14.5649C2.30703 16.2025 1.51979 17.5542 0.995052 18.6589C0.207863 20.9983 0.995052 23.1321 1.89981 23.6952C2.50188 24.0698 4.4678 24.0382 5.47201 23.7629C5.60851 23.7254 5.75527 23.7401 5.8771 23.8122C7.9215 25.0212 9.72421 24.8495 10.3567 24.493Z"
        fill="url(#toothGradientTopToBottom)"
        {...realisticStroke}
      />
    </>
  ),
  2: (
    <>
      <path
        d="M6.35475 23.6706C5.83397 23.5037 4.60717 24.3121 3.74132 24.5066C3.31467 24.6025 2.84807 24.4723 2.44169 24.3109C2.17459 24.2047 1.87707 24.0592 1.60134 23.9138C0.949548 23.5701 0.392938 23.0159 0.29419 22.2857C0.131888 21.0855 0.524277 19.6885 1.23965 18.4881C1.97878 17.2478 2.53313 15.5734 2.31755 13.6819C2.10197 11.7904 2.00958 8.25553 2.19436 7.13925C2.37914 6.02297 2.50233 1.7439 2.50233 1.37181C2.50233 1.07413 2.78977 0.999712 2.93349 0.999712C3.99292 1.22297 4.36043 4.66896 4.41176 6.36405C4.63761 8.5656 5.2556 12.9315 5.92082 12.7827C6.58604 12.6338 7.09112 8.10049 7.09112 8.10049C7.39909 5.55785 6.87554 1.7439 6.81394 1.46483C6.79217 1.3662 6.79364 1.25183 6.80561 1.14418C6.82974 0.927226 6.99304 0.711784 7.20195 0.775117C8.34066 1.12033 9.89646 6.04178 9.92446 10.271C9.94589 13.5082 10.2719 15.9083 10.4848 17.063C10.5377 17.3501 10.6586 17.6182 10.8125 17.8663C11.9192 19.6509 12.215 21.3849 12.2342 22.023C12.3846 22.7494 11.7907 23.5451 11.1649 24.0927C10.5478 24.6327 9.69555 24.6389 8.90693 24.4143C7.96466 24.146 6.7502 23.7973 6.35475 23.6706Z"
        fill="url(#toothGradientTopToBottom)"
        {...realisticStroke}
      />
    </>
  ),
  3: (
    <>
      <path
        d="M7.82637 28.2721C7.41642 28.1535 6.86442 27.8386 6.51535 27.6174C6.38861 27.5371 6.23134 27.5194 6.08982 27.5693C3.88188 28.3478 2.6738 27.6218 2.22387 27.0045C2.12718 26.8719 2.01964 26.7451 1.8919 26.642C0.800689 25.7612 0.74767 24.292 0.917921 23.3325C0.97805 22.9937 1.13065 22.6795 1.29932 22.3795C2.83956 19.6403 2.61216 18.6569 2.69341 18.0826C2.76185 17.5988 2.55083 13.9503 2.43676 12.1866C2.20809 10.5538 2.48429 5.59512 2.72192 3.32742C2.72192 1.83772 2.96455 1.24352 3.16608 1.0635C3.25141 0.987284 3.3812 0.978826 3.49164 1.00871C4.17538 1.1937 4.51598 3.12666 4.60399 4.14379C4.69524 11.2311 5.93475 13.9705 6.5431 14.4543C8.19704 14.7868 7.51265 1.18067 7.48413 0.999253C7.4702 0.910591 7.51231 0.847507 7.56236 0.807242C7.61726 0.763085 7.6913 0.752698 7.76154 0.758077C9.14882 0.864309 9.92417 4.76626 10.5639 7.86282C11.2198 11.0376 11.0487 16.6615 11.0772 17.3267C11.1 17.8588 11.5614 20.1588 11.8181 21.2573C12.0842 21.8117 12.8445 24.8796 12.8445 25.5811C12.7591 27.365 11.7046 27.1836 11.562 27.4859C11.4194 27.7883 10.4213 28.7559 9.36624 28.7559C8.31114 28.7559 8.45373 28.4535 7.82637 28.2721Z"
        fill="url(#toothGradientTopToBottom)"
        {...realisticStroke}
      />
    </>
  ),
  4: (
    <>
      <path
        d="M4.51202 28.7559C3.53456 28.7559 2.02572 27.4827 1.21799 26.7745C1.15798 26.7218 1.08359 26.69 1.01115 26.6565C0.277128 26.317 0.341915 24.2961 0.644697 23.71C1.7731 21.5253 1.75617 18.99 1.80131 17.0946C1.84644 15.1992 2.00818 13.2485 2.08341 12.51C2.84508 7.74078 2.70403 3.89464 2.6194 2.44849C2.55169 1.29157 2.98611 0.817263 3.43749 0.75619C4.17095 0.694652 5.67548 9.24847 6.25849 13.2177C6.43927 14.4484 7.15745 19.8946 7.49973 20.9715C7.9793 22.4804 8.25285 24.092 8.37272 25.0505C8.41645 25.4001 8.34172 25.7519 8.15833 26.0528C8.05201 26.2272 7.91696 26.443 7.75362 26.6946C7.44743 27.166 7.05063 27.4532 6.77859 27.6124C6.66268 27.6803 6.55848 27.7661 6.46542 27.8629C6.11298 28.2297 5.39408 28.7559 4.51202 28.7559Z"
        fill="url(#toothGradientTopToBottom)"
        {...realisticStroke}
      />
    </>
  ),
  5: (
    <>
      <path
        d="M8.00881 24.7586C8.12148 26.5846 6.98812 27.4533 6.16591 27.7389C5.95671 27.8116 5.76207 27.9275 5.60612 28.0847C4.60018 29.099 3.44518 28.7594 3.00764 28.3861C2.85985 28.3076 2.46454 28.139 2.06553 28.0919C1.66652 28.0449 1.068 27.5233 0.81862 27.2684C-0.289743 26.0918 -0.0126515 24.0917 0.264439 23.2387C0.54153 22.3857 1.05692 20.868 1.01258 19.6209C0.968249 18.3738 1.28967 15.0814 1.45593 13.5911C1.83462 11.042 2.53658 5.42011 2.31491 3.32588C2.09324 1.23165 2.592 0.74731 2.86909 0.766919C3.94974 0.590439 3.97108 2.57972 4.0265 2.90325C4.15082 3.62904 5.8343 12.797 6.22222 16.8854C6.52063 20.0305 7.5771 23.0287 7.9535 24.4313C7.98227 24.5385 8.00197 24.6478 8.00881 24.7586Z"
        fill="url(#toothGradientTopToBottom)"
        {...realisticStroke}
      />
    </>
  ),

  6: (
    <>
      <path
        d="M7.84263 28.149C7.47276 28.5149 6.89198 28.6833 6.55234 28.7386C6.4649 28.7528 6.3764 28.7521 6.28872 28.7394C5.68077 28.6519 4.42058 28.3264 3.32483 27.5698C1.84678 26.5492 1.06593 25.5286 0.675501 24.3977C0.365151 23.4988 1.1217 21.6394 1.73523 20.6188C2.22606 19.8023 2.29299 16.8215 2.2651 15.4331C2.15355 14.1367 2.60533 9.09451 2.96229 7.79258C3.2648 6.68925 3.01807 2.04311 2.9344 1.54646C2.87393 1.18748 3.28765 0.87526 3.46427 0.829288C5.08175 0.250162 5.6674 3.27164 5.83472 3.76543C6.16802 4.74902 7.27559 11.1302 7.67531 14.2471C7.85193 15.8469 8.42828 19.0466 9.32068 20.3706C10.2018 21.6779 10.4995 23.3413 10.5789 24.4276C10.6094 24.8444 10.5285 25.2632 10.3404 25.6364C9.39384 27.5136 8.32697 27.67 7.84263 28.149Z"
        fill="url(#toothGradientTopToBottom)"
        {...realisticStroke}
      />
    </>
  ),
  7: (
    <>
      <path
        d="M5.14622 23.6676L5.0733 23.6547C4.32855 23.5231 3.53566 23.7551 2.80738 23.5511C2.45273 23.4518 2.17813 23.2092 1.82417 22.9123C1.30731 22.4789 1.04104 21.3872 0.97252 20.8956C0.972526 19.7217 1.17222 17.8133 1.26619 17.0427C1.38366 16.0795 1.41303 15.4775 1.26619 13.0393C1.11936 10.6012 1.94194 7.56099 2.32371 6.23656C2.70549 4.91213 2.67642 2.56428 2.52928 1.26995C2.43808 0.467689 2.83244 0.336818 3.05759 0.336814C3.83288 0.16825 4.69236 2.49403 5.02519 3.67799C5.26013 4.64121 5.73587 6.88672 5.75937 8.16299C5.78091 9.33306 6.40497 11.8407 6.78023 13.2318C6.84306 13.4647 6.93323 13.6878 7.05114 13.8982C7.55413 14.796 8.43446 16.3215 8.81385 17.5845C9.28404 19.1498 9.53163 21.6063 9.3424 22.521C9.16427 23.3821 7.34564 23.64 6.14393 23.7261C5.81024 23.75 5.47567 23.7258 5.14622 23.6676Z"
        fill="url(#toothGradientTopToBottom)"
        {...realisticStroke}
      />
    </>
  ),
  8: (
    <>
      <path
        d="M10.7281 26.8501C10.6861 27.8443 10.7281 28.032 10.4449 28.343C10.1564 28.6599 8.10593 28.8407 5.54607 28.7163C2.34625 28.5608 1.77991 27.8143 1.12862 26.4147C0.101032 24.2065 1.64046 20.0704 1.72328 19.4791C1.83655 18.6704 2.01589 15.6121 2.20467 13.9741C2.46896 12.222 3.04852 8.43798 3.2524 7.31832C3.50725 5.91875 3.45062 2.24875 3.4223 1.53341C3.40268 1.03778 3.82062 0.832978 4.11687 0.773542C4.20038 0.756786 4.28688 0.757195 4.36831 0.782194C5.96077 1.2711 7.30819 7.50109 7.55658 9.74424C7.81143 12.0458 9.5954 19.448 9.7653 19.7901C9.93521 20.1322 10.3317 21.1585 10.5865 22.216C10.8414 23.2735 10.7847 25.5128 10.7281 26.8501Z"
        fill="url(#toothGradientTopToBottom)"
        {...realisticStroke}
      />
    </>
  ),
  9: (
    <>
      <path
        d="M0.410564 26.8501C0.452597 27.8443 0.410552 28.032 0.693734 28.343C0.982226 28.6599 3.03274 28.8407 5.5926 28.7163C8.79242 28.5608 9.35876 27.8143 10.01 26.4147C11.0376 24.2065 9.49821 20.0704 9.41539 19.4791C9.30212 18.6704 9.12278 15.6121 8.934 13.9741C8.66971 12.222 8.09016 8.43798 7.88627 7.31832C7.63142 5.91875 7.68806 2.24875 7.71637 1.53341C7.73599 1.03778 7.31806 0.832978 7.02181 0.773542C6.93829 0.756786 6.85179 0.757195 6.77036 0.782194C5.1779 1.2711 3.83048 7.50109 3.58209 9.74424C3.32724 12.0458 1.54327 19.448 1.37337 19.7901C1.20347 20.1322 0.807002 21.1585 0.552149 22.216C0.297296 23.2735 0.354021 25.5128 0.410564 26.8501Z"
        fill="url(#toothGradientTopToBottom)"
        {...realisticStroke}
      />
    </>
  ),
  10: (
    <>
      <path
        d="M4.99245 23.6676L5.06537 23.6547C5.81013 23.5231 6.60301 23.7551 7.33129 23.5511C7.68594 23.4518 7.96054 23.2092 8.3145 22.9123C8.83137 22.4789 9.09763 21.3872 9.16615 20.8956C9.16615 19.7217 8.96646 17.8133 8.87248 17.0427C8.75501 16.0795 8.72564 15.4775 8.87248 13.0393C9.01932 10.6012 8.19673 7.56099 7.81496 6.23656C7.43319 4.91213 7.46225 2.56428 7.60939 1.26995C7.70059 0.467689 7.30623 0.336818 7.08108 0.336814C6.30579 0.16825 5.44631 2.49403 5.11348 3.67799C4.87855 4.64121 4.4028 6.88672 4.3793 8.16299C4.35777 9.33306 3.7337 11.8407 3.35844 13.2318C3.29561 13.4647 3.20545 13.6878 3.08753 13.8982C2.58454 14.796 1.70421 16.3215 1.32482 17.5845C0.854629 19.1498 0.607043 21.6063 0.796276 22.521C0.974401 23.3821 2.79303 23.64 3.99474 23.7261C4.32843 23.75 4.663 23.7258 4.99245 23.6676Z"
        fill="url(#toothGradientTopToBottom)"
        {...realisticStroke}
      />
    </>
  ),
  11: (
    <>
      <path
        d="M3.29604 28.149C3.66591 28.5149 4.2467 28.6833 4.58634 28.7386C4.67377 28.7528 4.76227 28.7521 4.84995 28.7394C5.4579 28.6519 6.71809 28.3264 7.81384 27.5698C9.29189 26.5492 10.0727 25.5286 10.4632 24.3977C10.7735 23.4988 10.017 21.6394 9.40344 20.6188C8.91262 19.8023 8.84569 16.8215 8.87357 15.4331C8.98512 14.1367 8.53334 9.09451 8.17638 7.79258C7.87387 6.68925 8.12061 2.04311 8.20427 1.54646C8.26474 1.18748 7.85103 0.87526 7.6744 0.829288C6.05692 0.250162 5.47128 3.27164 5.30395 3.76543C4.97065 4.74902 3.86309 11.1302 3.46336 14.2471C3.28674 15.8469 2.7104 19.0466 1.81799 20.3706C0.936827 21.6779 0.639153 23.3413 0.559758 24.4276C0.529294 24.8444 0.610137 25.2632 0.798304 25.6364C1.74483 27.5136 2.8117 27.67 3.29604 28.149Z"
        fill="url(#toothGradientTopToBottom)"
        {...realisticStroke}
      />
    </>
  ),
  12: (
    <>
      <path
        d="M0.129862 24.7586C0.0171883 26.5846 1.15055 27.4533 1.97276 27.7389C2.18196 27.8116 2.3766 27.9275 2.53256 28.0847C3.5385 29.099 4.6935 28.7594 5.13104 28.3861C5.27882 28.3076 5.67413 28.139 6.07314 28.0919C6.47215 28.0449 7.07067 27.5233 7.32005 27.2684C8.42841 26.0918 8.15132 24.0917 7.87423 23.2387C7.59714 22.3857 7.08175 20.868 7.12609 19.6209C7.17042 18.3738 6.849 15.0814 6.68274 13.5911C6.30405 11.042 5.60209 5.42011 5.82376 3.32588C6.04544 1.23165 5.54667 0.74731 5.26958 0.766919C4.18893 0.590439 4.16759 2.57972 4.11217 2.90325C3.98786 3.62904 2.30437 12.797 1.91645 16.8854C1.61804 20.0305 0.561571 23.0287 0.185177 24.4313C0.156404 24.5385 0.136699 24.6478 0.129862 24.7586Z"
        fill="url(#toothGradientTopToBottom)"
        {...realisticStroke}
      />
    </>
  ),
  13: (
    <>
      <path
        d="M4.62665 28.7559C5.60411 28.7559 7.11296 27.4827 7.92068 26.7745C7.98069 26.7218 8.05508 26.69 8.12752 26.6565C8.86154 26.317 8.79676 24.2961 8.49397 23.71C7.36557 21.5253 7.3825 18.99 7.33736 17.0946C7.29223 15.1992 7.13049 13.2485 7.05526 12.51C6.29359 7.74078 6.43464 3.89464 6.51927 2.44849C6.58698 1.29157 6.15256 0.817263 5.70118 0.75619C4.96772 0.694652 3.46319 9.24847 2.88018 13.2177C2.6994 14.4484 1.98122 19.8946 1.63894 20.9715C1.15937 22.4804 0.885825 24.092 0.765947 25.0505C0.722217 25.4001 0.796956 25.7519 0.980347 26.0528C1.08666 26.2272 1.22171 26.443 1.38505 26.6946C1.69124 27.166 2.08804 27.4532 2.36008 27.6124C2.47599 27.6803 2.58019 27.7661 2.67325 27.8629C3.02569 28.2297 3.7446 28.7559 4.62665 28.7559Z"
        fill="url(#toothGradientTopToBottom)"
        {...realisticStroke}
      />
    </>
  ),
  14: (
    <>
      <path
        d="M5.31328 28.2721C5.72322 28.1535 6.27523 27.8386 6.6243 27.6174C6.75104 27.5371 6.90831 27.5194 7.04983 27.5693C9.25777 28.3478 10.4658 27.6218 10.9158 27.0045C11.0125 26.8719 11.12 26.7451 11.2477 26.642C12.339 25.7612 12.392 24.292 12.2217 23.3325C12.1616 22.9937 12.009 22.6795 11.8403 22.3795C10.3001 19.6403 10.5275 18.6569 10.4462 18.0826C10.3778 17.5988 10.5888 13.9503 10.7029 12.1866C10.9316 10.5538 10.6554 5.59512 10.4177 3.32742C10.4177 1.83772 10.1751 1.24352 9.97356 1.0635C9.88824 0.987284 9.75844 0.978826 9.64801 1.00871C8.96427 1.1937 8.62366 3.12666 8.53566 4.14379C8.4444 11.2311 7.20489 13.9705 6.59655 14.4543C4.94261 14.7868 5.627 1.18067 5.65551 0.999253C5.66945 0.910591 5.62734 0.847507 5.57728 0.807242C5.52239 0.763085 5.44835 0.752698 5.3781 0.758077C3.99083 0.864309 3.21548 4.76626 2.57577 7.86282C1.91989 11.0376 2.09099 16.6615 2.06248 17.3267C2.03966 17.8588 1.57824 20.1588 1.32159 21.2573C1.05544 21.8117 0.295145 24.8796 0.295145 25.5811C0.380593 27.365 1.43508 27.1836 1.57766 27.4859C1.72024 27.7883 2.71831 28.7559 3.77341 28.7559C4.8285 28.7559 4.68592 28.4535 5.31328 28.2721Z"
        fill="url(#toothGradientTopToBottom)"
        {...realisticStroke}
      />
    </>
  ),
  15: (
    <>
      <path
        d="M6.78392 23.6706C7.3047 23.5037 8.5315 24.3121 9.39735 24.5066C9.824 24.6025 10.2906 24.4723 10.697 24.3109C10.9641 24.2047 11.2616 24.0592 11.5373 23.9138C12.1891 23.5701 12.7457 23.0159 12.8445 22.2857C13.0068 21.0855 12.6144 19.6885 11.899 18.4881C11.1599 17.2478 10.6055 15.5734 10.8211 13.6819C11.0367 11.7904 11.1291 8.25553 10.9443 7.13925C10.7595 6.02297 10.6363 1.7439 10.6363 1.37181C10.6363 1.07413 10.3489 0.999712 10.2052 0.999712C9.14575 1.22297 8.77824 4.66896 8.72691 6.36405C8.50107 8.5656 7.88307 12.9315 7.21785 12.7827C6.55263 12.6338 6.04756 8.10049 6.04756 8.10049C5.73958 5.55785 6.26314 1.7439 6.32473 1.46483C6.3465 1.3662 6.34503 1.25183 6.33306 1.14418C6.30894 0.927226 6.14563 0.711784 5.93672 0.775117C4.79801 1.12033 3.24221 6.04178 3.21421 10.271C3.19278 13.5082 2.86676 15.9083 2.65392 17.063C2.60099 17.3501 2.48003 17.6182 2.32614 17.8663C1.21945 19.6509 0.923626 21.3849 0.904423 22.023C0.75411 22.7494 1.34797 23.5451 1.97379 24.0927C2.59086 24.6327 3.44312 24.6389 4.23175 24.4143C5.17401 24.146 6.38847 23.7973 6.78392 23.6706Z"
        fill="url(#toothGradientTopToBottom)"
        {...realisticStroke}
      />
    </>
  ),
  16: (
    <>
      <path
        d="M2.78199 24.493C0.879637 23.4207 0.456068 22.1464 0.467001 20.8359C0.467001 19.7181 0.816846 18.9838 0.991769 18.7564C1.98888 17.3787 2.34748 15.6263 2.40215 14.9223C2.81761 12.8104 3.70101 8.2485 3.91093 6.89683C4.12085 5.54517 4.80745 3.14943 5.12451 2.12052C5.28851 1.66563 5.74114 0.755859 6.2397 0.755859C6.59354 0.755859 6.78106 1.08321 6.86793 1.3894C6.93101 1.61176 6.91834 1.84422 6.9009 2.07469C6.70506 4.66262 6.54909 9.8536 6.92849 9.8536C7.9452 9.8536 8.20767 3.4202 8.63406 2.44544C8.88083 1.88131 9.22259 1.65727 9.50037 1.58879C9.68307 1.54375 9.86424 1.62682 9.92848 1.80368C10.2116 2.58324 10.4052 4.97981 10.4052 4.97981C10.7987 7.35172 11.0435 13.5577 10.9628 14.5649C10.8316 16.2025 11.6189 17.5542 12.1436 18.6589C12.9308 20.9983 12.1436 23.1321 11.2389 23.6952C10.6368 24.0698 8.67088 24.0382 7.66666 23.7629C7.53016 23.7254 7.3834 23.7401 7.26157 23.8122C5.21717 25.0212 3.41446 24.8495 2.78199 24.493Z"
        fill="url(#toothGradientTopToBottom)"
        {...realisticStroke}
      />
    </>
  ),
  17: (
    <>
      <path
        d="M5.62193 1.01171C3.62871 -0.280283 1.89726 1.21624 1.60709 1.49054C1.57749 1.51852 1.54909 1.54421 1.51808 1.57063C1.28447 1.7696 0.254251 2.71203 0.437796 3.7125C0.644548 4.83948 1.08009 8.84136 0.994626 9.98539C0.90916 11.1294 0.923405 12.4832 0.994626 13.6498C1.35073 20.6871 3.27372 24.074 4.41326 23.9988C5.55281 23.9235 6.16673 17.0034 6.27356 17.0034C6.34072 17.0034 6.50023 17.6217 6.61732 17.984C6.66573 18.1338 6.7197 18.2815 6.74974 18.436C7.04118 19.9349 7.34185 23.5278 8.25923 23.5848C9.22784 23.645 9.73114 20.0474 9.86172 18.3163L10.289 12.709C10.3175 11.0231 11.0047 7.66628 11.6338 6.19862L12.2749 4.35463C12.7734 1.68272 11.8309 1.20122 11.3162 1.05C10.8016 0.898775 11.1356 0.263107 9.38233 0.0268905C8.0534 -0.152158 6.78398 0.606894 6.22553 1.00346C6.0479 1.1296 5.80474 1.13021 5.62193 1.01171Z"
        fill="url(#toothGradientBottomToTop)"
        {...realisticStroke}
      />
    </>
  ),
  18: (
    <>
      <path
        d="M11.1669 23.2359C9.7166 23.2359 8.36049 16.4647 7.87911 13.111C7.47972 11.4554 7.1416 14.3846 7.1416 14.4483C6.61925 18.8739 7.69469 21.9941 7.87911 22.822C8.02664 23.4842 7.75623 24 7.57178 24C5.57453 23.9363 3.48511 17.7277 3.02421 14.6074C2.72719 12.1771 1.94263 7.01699 1.1806 5.81984C0.418577 4.62269 0.119981 3.77152 0.0790119 3.52742C-0.166802 2.22202 0.658247 0.407184 2.19459 0.0569551C3.24944 -0.183513 4.40992 0.387479 5.07384 0.835979C5.24633 0.952506 5.47452 0.954815 5.6462 0.837099C6.10558 0.522136 6.81373 0.108101 7.35669 0.0569551C7.84762 0.0107102 8.47911 0.280481 8.91248 0.520906C9.08715 0.617808 9.30218 0.599312 9.4805 0.509297C9.86296 0.31623 10.258 0.334162 10.5524 0.424962C10.7674 0.49128 10.9369 0.652709 11.0629 0.839068C12.2916 2.65545 11.2803 4.71031 11.2283 5.43777C11.1791 6.1255 11.1054 7.28444 11.1054 8.08042C11.1668 9.25847 11.6279 14.1617 11.9043 17.2819C12.3272 22.0549 11.638 23.2571 11.1669 23.2359Z"
        fill="url(#toothGradientBottomToTop)"
        {...realisticStroke}
      />
    </>
  ),
  19: (
    <>
      <path
        d="M10.2132 22.7577C10.1397 23.3527 9.9441 23.6843 9.76307 23.8528C9.61699 23.9888 9.39955 23.9537 9.24504 23.8274C7.31319 22.2477 6.86213 15.66 6.77977 14.6035C6.7428 14.1291 6.69587 13.8873 6.63362 13.7695C6.56335 13.6365 6.39721 13.7553 6.33468 13.8921C4.30057 18.3417 4.6048 20.4088 4.57665 21.7977C4.56132 22.5538 4.35706 22.905 4.15789 23.046C4.00408 23.1549 3.80318 23.0806 3.66279 22.9548C2.20963 21.653 1.07064 16.4997 1.1432 12.9377V5.22974C1.12413 5.0321 1.00633 4.08021 0.731645 3.31225C0.570931 2.86293 0.697388 2.35229 0.85708 1.81341C1.15692 0.801631 2.21705 0.15165 2.73556 0.0664081C3.18182 -0.00695692 4.17608 0.260534 4.49081 0.345236C4.68931 0.398656 5.20906 0.701245 5.59308 0.937606C5.75662 1.03827 5.96452 1.02844 6.11903 0.91438C6.58171 0.572817 7.33742 0.0761299 8.06723 0.00643674C8.88346 -0.0715076 9.84529 0.575911 10.4394 1.00237C10.5252 1.06391 10.6407 1.05717 10.7274 0.99695C11.1786 0.683469 11.6495 0.700977 12.0424 0.907545C12.1414 0.959598 12.222 1.0406 12.2778 1.13758C12.8519 2.1365 12.6308 3.68032 12.5308 4.32625L11.5007 7.77081C10.7683 11.2493 10.4707 16.9353 10.4135 19.7964C10.3944 20.8504 10.3276 21.8316 10.2132 22.7577Z"
        fill="url(#toothGradientBottomToTop)"
        {...realisticStroke}
      />
    </>
  ),
  20: (
    <>
      <path
        d="M6.96858 26.645C6.99128 27.5566 6.59032 27.9897 6.3728 28C5.5829 28 2.85474 17.1911 2.59939 13.5573C2.38876 10.5598 1.80499 8.29146 1.69151 7.70636C1.57802 7.12127 0.691903 5.55074 0.453584 4.25737C0.314481 3.50245 -0.0391426 1.8246 0.953855 1.2395C1.62754 0.842546 2.06034 0.931549 2.76962 0.377246C3.33705 -0.0661958 3.88556 -0.043614 4.15982 0.0692995C4.8691 0.500427 6.08907 1.2395 6.6565 1.2395C7.29565 1.2395 7.78191 1.4448 7.87648 1.54744C8.52902 2.13254 8.18856 3.54526 8.1886 4.04181C8.18863 4.50372 7.76303 5.7663 7.64954 6.65935C7.55875 7.37378 7.47933 10.4676 7.45097 11.9252C7.53608 13.2494 6.6565 19.8702 6.59976 21.6563C6.54301 23.4424 6.94021 25.5056 6.96858 26.645Z"
        fill="url(#toothGradientBottomToTop)"
        {...realisticStroke}
      />
    </>
  ),
  21: (
    <>
      <path
        d="M6.79719 26.8814C6.76119 27.4808 6.34289 27.7962 6.01032 27.9301C5.86741 27.9877 5.70859 27.9339 5.62849 27.8023C4.19513 25.447 2.72733 11.2744 2.56239 10.3035C2.39186 9.2996 1.82342 7.26321 1.19815 5.28418C0.697924 3.70095 0.932877 2.69327 1.11288 2.38734C2.76134 0.350943 3.98347 0.0641267 4.52349 0.0067635C4.9555 -0.0391271 5.32877 0.159732 5.4614 0.264898C5.8972 0.551714 6.97225 1.0454 7.30881 1.12535C7.79182 1.24007 8.18974 1.58425 8.36025 1.84239C9.26972 3.2192 8.78671 5.48495 8.61618 6.00122C8.47975 6.41423 8.4646 7.30145 8.47407 7.69343C8.36025 11.2499 7.86585 16.4356 7.25194 19.2808C6.48455 22.8373 6.85403 25.9349 6.79719 26.8814Z"
        fill="url(#toothGradientBottomToTop)"
        {...realisticStroke}
      />
    </>
  ),
  22: (
    <>
      <path
        d="M5.55232 27.2735C5.57105 27.7176 5.21385 27.9121 4.95892 27.9763C4.86936 27.9988 4.76942 28.0003 4.697 27.943C3.49958 26.9954 2.63186 14.7604 2.21238 11.678C2.19325 11.5374 2.15018 11.4074 2.08918 11.2792C0.496128 7.93252 0.444154 4.08118 0.554348 2.26952C0.6442 0.792291 2.29524 0.180814 3.10953 0.0597293C3.67109 -0.121898 5.31087 0.00524128 7.37747 1.96681C9.38381 3.87118 8.30609 8.23114 7.4536 10.3489C7.40403 10.472 7.3703 10.5995 7.35578 10.7315C7.08197 13.2178 6.47321 18.3477 6.05777 20.4928C5.52427 23.2474 5.52424 26.6075 5.55232 27.2735Z"
        fill="url(#toothGradientBottomToTop)"
        {...realisticStroke}
      />
    </>
  ),
  23: (
    <>
      <path
        d="M7.18001 17.8136C7.13127 18.9165 7.14285 21.5125 7.34327 23.0144C7.38114 23.2983 7.28855 23.5873 7.07816 23.7816C6.92609 23.922 6.72014 24.0126 6.52868 23.934C6.00853 23.7203 5.55089 23.0394 5.35299 22.6565C4.0673 20.8131 3.55984 18.5323 2.91701 16.064C2.42519 14.1755 2.01795 10.7648 1.8551 9.09516C1.84097 8.95032 1.8042 8.80926 1.74662 8.67561C0.570328 5.94529 0.186731 4.26205 0.142697 3.75375C0.165252 3.03513 0.19683 1.45417 0.142697 0.879278C0.0885639 0.304384 0.593805 0.160661 0.853192 0.160661C1.13513 0.119001 2.06442 0.0294331 3.52601 0.00443773C5.35299 -0.0268065 6.84168 0.0981704 7.78901 0.660566C8.73633 1.22296 7.48451 5.56591 7.24768 6.1283C7.05821 6.57822 6.98829 7.29476 6.97701 7.59678C7.06724 10.4921 7.23415 16.5889 7.18001 17.8136Z"
        fill="url(#toothGradientBottomToTop)"
        {...realisticStroke}
      />
    </>
  ),
  24: (
    <>
      <path
        d="M7.95179 23.2786C8.03227 23.8306 7.62763 23.9895 7.41525 24C6.10963 24 4.39719 19.0442 3.62592 16.7545C2.85464 14.4647 2.72051 9.41482 2.4187 8.7875C2.10672 8.13901 1.41269 5.87046 1.01029 4.74128C0.607887 3.61211 0.80909 1.44785 0.80909 0.914632C0.80909 0.564591 0.80909 0.144214 2.65344 0.0363874C4.79959 -0.0890839 7.01281 0.130485 7.91822 0.381413C8.64255 0.582156 8.77892 1.30148 8.75656 1.63605C8.58889 2.75477 8.18649 5.1365 7.91822 5.71363C7.64995 6.29077 7.53817 7.50149 7.51581 8.03471C7.66114 10.7217 7.91825 16.4408 7.78408 17.8209C7.61637 19.546 7.85119 22.5885 7.95179 23.2786Z"
        fill="url(#toothGradientBottomToTop)"
        {...realisticStroke}
      />
    </>
  ),
  25: (
    <>
      <path
        d="M1.18688 23.2786C1.1064 23.8306 1.51104 23.9895 1.72342 24C3.02904 24 4.74148 19.0442 5.51276 16.7545C6.28403 14.4647 6.41816 9.41482 6.71997 8.7875C7.03195 8.13901 7.72598 5.87046 8.12838 4.74128C8.53078 3.61211 8.32958 1.44785 8.32958 0.914632C8.32958 0.564591 8.32958 0.144214 6.48523 0.0363874C4.33908 -0.0890839 2.12586 0.130485 1.22045 0.381413C0.496127 0.582156 0.359757 1.30148 0.382112 1.63605C0.549781 2.75477 0.952184 5.1365 1.22045 5.71363C1.48872 6.29077 1.6005 7.50149 1.62286 8.03471C1.47753 10.7217 1.22042 16.4408 1.35459 17.8209C1.5223 19.546 1.28748 22.5885 1.18688 23.2786Z"
        fill="url(#toothGradientBottomToTop)"
        {...realisticStroke}
      />
    </>
  ),
  26: (
    <>
      <path
        d="M0.958659 17.8136C1.0074 18.9165 0.995823 21.5125 0.795406 23.0144C0.757527 23.2983 0.850118 23.5873 1.06051 23.7816C1.21258 23.922 1.41853 24.0126 1.61 23.934C2.13015 23.7203 2.58779 23.0394 2.78568 22.6565C4.07137 20.8131 4.57883 18.5323 5.22166 16.064C5.71348 14.1755 6.12072 10.7648 6.28357 9.09516C6.2977 8.95032 6.33447 8.80926 6.39205 8.67561C7.56834 5.94529 7.95194 4.26205 7.99598 3.75375C7.97342 3.03513 7.94184 1.45417 7.99598 0.879278C8.05011 0.304384 7.54487 0.160661 7.28548 0.160661C7.00354 0.119001 6.07426 0.0294331 4.61267 0.00443773C2.78568 -0.0268065 1.29699 0.0981704 0.349664 0.660566C-0.597663 1.22296 0.654162 5.56591 0.890993 6.1283C1.08046 6.57822 1.15038 7.29476 1.16166 7.59678C1.07144 10.4921 0.904526 16.5889 0.958659 17.8136Z"
        fill="url(#toothGradientBottomToTop)"
        {...realisticStroke}
      />
    </>
  ),
  27: (
    <>
      <path
        d="M3.58635 27.2735C3.56762 27.7176 3.92483 27.9121 4.17975 27.9763C4.26932 27.9988 4.36925 28.0003 4.44167 27.943C5.6391 26.9954 6.50681 14.7604 6.92629 11.678C6.94543 11.5374 6.9885 11.4074 7.04949 11.2792C8.64254 7.93252 8.69452 4.08118 8.58432 2.26952C8.49447 0.792291 6.84343 0.180814 6.02914 0.0597293C5.46758 -0.121898 3.82781 0.00524128 1.7612 1.96681C-0.245143 3.87118 0.832583 8.23114 1.68508 10.3489C1.73464 10.472 1.76837 10.5995 1.7829 10.7315C2.0567 13.2178 2.66547 18.3477 3.08091 20.4928C3.61441 23.2474 3.61443 26.6075 3.58635 27.2735Z"
        fill="url(#toothGradientBottomToTop)"
        {...realisticStroke}
      />
    </>
  ),
  28: (
    <>
      <path
        d="M2.34148 26.8814C2.37748 27.4808 2.79578 27.7962 3.12835 27.9301C3.27126 27.9877 3.43009 27.9339 3.51018 27.8023C4.94354 25.447 6.41134 11.2744 6.57628 10.3035C6.74681 9.2996 7.31525 7.26321 7.94053 5.28418C8.44075 3.70095 8.2058 2.69327 8.02579 2.38734C6.37733 0.350943 5.1552 0.0641267 4.61519 0.0067635C4.18318 -0.0391271 3.8099 0.159732 3.67727 0.264898C3.24147 0.551714 2.16643 1.0454 1.82986 1.12535C1.34685 1.24007 0.948934 1.58425 0.778421 1.84239C-0.131047 3.2192 0.351963 5.48495 0.522493 6.00122C0.658917 6.41423 0.674075 7.30145 0.664601 7.69343C0.778421 11.2499 1.27283 16.4356 1.88674 19.2808C2.65412 22.8373 2.28464 25.9349 2.34148 26.8814Z"
        fill="url(#toothGradientBottomToTop)"
        {...realisticStroke}
      />
    </>
  ),
  29: (
    <>
      <path
        d="M2.17009 26.645C2.14739 27.5566 2.54835 27.9897 2.76587 28C3.55577 28 6.28394 17.1911 6.53928 13.5573C6.74991 10.5598 7.33368 8.29146 7.44716 7.70636C7.56065 7.12127 8.44677 5.55074 8.68509 4.25737C8.82419 3.50245 9.17781 1.8246 8.18482 1.2395C7.51113 0.842546 7.07833 0.931549 6.36905 0.377246C5.80162 -0.0661958 5.25311 -0.043614 4.97885 0.0692995C4.26957 0.500427 3.0496 1.2395 2.48217 1.2395C1.84302 1.2395 1.35677 1.4448 1.26219 1.54744C0.609649 2.13254 0.950108 3.54526 0.950073 4.04181C0.950039 4.50372 1.37564 5.7663 1.48913 6.65935C1.57992 7.37378 1.65934 10.4676 1.6877 11.9252C1.60259 13.2494 2.48217 19.8702 2.53892 21.6563C2.59566 23.4424 2.19846 25.5056 2.17009 26.645Z"
        fill="url(#toothGradientBottomToTop)"
        {...realisticStroke}
      />
    </>
  ),
  30: (
    <>
      <path
        d="M2.92548 22.7577C2.99901 23.3527 3.19457 23.6843 3.3756 23.8528C3.52168 23.9888 3.73913 23.9537 3.89363 23.8274C5.82548 22.2477 6.27654 15.66 6.3589 14.6035C6.39587 14.1291 6.44281 13.8873 6.50505 13.7695C6.57532 13.6365 6.74146 13.7553 6.80399 13.8921C8.8381 18.3417 8.53387 20.4088 8.56202 21.7977C8.57735 22.5538 8.78161 22.905 8.98078 23.046C9.13459 23.1549 9.3355 23.0806 9.47588 22.9548C10.929 21.653 12.068 16.4997 11.9955 12.9377V5.22974C12.0145 5.0321 12.1323 4.08021 12.407 3.31225C12.5677 2.86293 12.4413 2.35229 12.2816 1.81341C11.9818 0.801631 10.9216 0.15165 10.4031 0.0664081C9.95685 -0.00695692 8.96259 0.260534 8.64786 0.345236C8.44937 0.398656 7.92961 0.701245 7.54559 0.937606C7.38205 1.03827 7.17415 1.02844 7.01964 0.91438C6.55696 0.572817 5.80126 0.0761299 5.07144 0.00643674C4.25521 -0.0715076 3.29338 0.575911 2.69922 1.00237C2.61348 1.06391 2.49795 1.05717 2.41128 0.99695C1.96011 0.683469 1.48918 0.700977 1.09631 0.907545C0.997309 0.959598 0.916661 1.0406 0.860921 1.13758C0.286729 2.1365 0.507898 3.68032 0.607902 4.32625L1.63794 7.77081C2.3704 11.2493 2.66797 16.9353 2.72519 19.7964C2.74427 20.8504 2.81103 21.8316 2.92548 22.7577Z"
        fill="url(#toothGradientBottomToTop)"
        {...realisticStroke}
      />
    </>
  ),
  31: (
    <>
      <path
        d="M0.971767 23.2359C2.42207 23.2359 3.77818 16.4647 4.25957 13.111C4.65896 11.4554 4.99707 14.3846 4.99707 14.4483C5.51943 18.8739 4.44399 21.9941 4.25957 22.822C4.11203 23.4842 4.38244 24 4.56689 24C6.56414 23.9363 8.65356 17.7277 9.11446 14.6074C9.41149 12.1771 10.196 7.01699 10.9581 5.81984C11.7201 4.62269 12.0187 3.77152 12.0597 3.52742C12.3055 2.22202 11.4804 0.407184 9.94408 0.0569551C8.88923 -0.183513 7.72876 0.387479 7.06483 0.835979C6.89234 0.952506 6.66416 0.954815 6.49247 0.837099C6.0331 0.522136 5.32494 0.108101 4.78198 0.0569551C4.29105 0.0107102 3.65956 0.280481 3.22619 0.520906C3.05152 0.617808 2.83649 0.599312 2.65817 0.509297C2.27571 0.31623 1.88063 0.334162 1.58627 0.424962C1.37128 0.49128 1.20179 0.652709 1.07574 0.839068C-0.152898 2.65545 0.858398 4.71031 0.910402 5.43777C0.959565 6.1255 1.03331 7.28444 1.03331 8.08042C0.971856 9.25847 0.510822 14.1617 0.234353 17.2819C-0.188561 22.0549 0.500622 23.2571 0.971767 23.2359Z"
        fill="url(#toothGradientBottomToTop)"
        {...realisticStroke}
      />
    </>
  ),
  32: (
    <>
      <path
        d="M7.51674 1.01171C9.50996 -0.280283 11.2414 1.21624 11.5316 1.49054C11.5612 1.51852 11.5896 1.54421 11.6206 1.57063C11.8542 1.7696 12.8844 2.71203 12.7009 3.7125C12.4941 4.83948 12.0586 8.84136 12.144 9.98539C12.2295 11.1294 12.2153 12.4832 12.144 13.6498C11.7879 20.6871 9.86496 24.074 8.72541 23.9988C7.58587 23.9235 6.97194 17.0034 6.86511 17.0034C6.79795 17.0034 6.63844 17.6217 6.52135 17.984C6.47294 18.1338 6.41898 18.2815 6.38893 18.436C6.09749 19.9349 5.79682 23.5278 4.87944 23.5848C3.91083 23.645 3.40753 20.0474 3.27696 18.3163L2.84963 12.709C2.82114 11.0231 2.13395 7.66628 1.50483 6.19862L0.863816 4.35463C0.365265 1.68272 1.30779 1.20122 1.82243 1.05C2.33706 0.898775 2.00311 0.263107 3.75634 0.0268905C5.08527 -0.152158 6.35469 0.606894 6.91314 1.00346C7.09077 1.1296 7.33393 1.13021 7.51674 1.01171Z"
        fill="url(#toothGradientBottomToTop)"
        {...realisticStroke}
      />
    </>
  ),
}

const IPRToothIcon: React.FC<IPRToothIconProps> = ({
  id,
  active,
  width = 102,
  height = 102,
}) => {
  const { stroke } = getIPRColor(active)
  const pathGroupRef = useRef<SVGGElement>(null)
  const [offset, setOffset] = useState(0)

  useEffect(() => {
    if (pathGroupRef.current) {
      const bbox = pathGroupRef.current.getBBox()
      const isLowerArch = id >= 17

      const shift = isLowerArch ? 0 - bbox.y : 32 - (bbox.y + bbox.height)

      setOffset(shift)
    }
  }, [])

  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-block',
        width,
        height,
      }}
    >
      <svg
        role="img"
        aria-label={`Tooth icon ${id}`}
        width={width}
        height={height}
        viewBox="0 0 13 32"
        stroke={stroke}
        xmlns="http://www.w3.org/2000/svg"
      >
        <GradientDefs id={id} />
        <g ref={pathGroupRef} transform={`translate(0, ${offset})`}>
          {toothIcons[id]}
        </g>
        <title>Tooth {id}</title>
      </svg>
    </div>
  )
}

export default IPRToothIcon
